<template>
  <SectionTemplate
    title="生成数字人视频，就像打字一样简单"
    sub-title="零门槛创作"
    :desc="descText"
    theme="dark"
    class="gen-like-typing-section"
  >
    <div class="gen-trail">
      <div class="gen-menu">
        <p class="gen-title">免费体验演示效果</p>
        <div class="gen-trail-section">
          <p class="gen-section-title">1.选择人物和场景</p>
          <div class="gen-section-selectors">
            <ImageSelector
              v-for="(image, index) in avatarImages"
              :key="index"
              :image-src="image.src"
              :alt="image.alt"
              :is-selected="selectedAvatar === image.key"
              @select="handleSelectAvatar(image.key)"
            />
            <ImageSelector
              v-for="(image, index) in sceneImages"
              :key="index"
              :image-src="image.src"
              :alt="image.alt"
              :is-selected="selectedScene === image.key"
              innerBorderClass="box-white"
              @select="handleSelectScene(image.key)"
            />
          </div>
        </div>

        <div class="gen-trail-section">
          <p class="gen-section-title">2.输入你的视频脚本</p>
          <a-textarea
            v-model:value="videoScript"
            :rows="4"
            :maxlength="100"
            :show-count="true"
            placeholder="在这里输入你想让数字人说的话"
            @change="
              () => {
                isModify = true;
              }
            "
            class="gen-section-textarea"
          />
        </div>
        <BaseButton class="gen-trail-btn" variant="purple-large" @click.stop="handleGenerate"
          >3D生成
          <template #icon>
            <IconMoreRight class="gtn-trail-btn__icon" />
          </template>
        </BaseButton>
      </div>
      <div class="gen-content">
        <Transition name="video-fade" mode="out-in">
          <VideoPlayer
            :key="`${isGenerated ? 'generate' : 'standby'}-${selectedAvatar}${selectedScene}`"
            :url="isGenerated ? generateVideoSrc : standbyVideoSrc"
            :poster="isGenerated ? generatePosterSrc : standbyPosterSrc"
            object-fit="cover"
            :muted="!isGenerated"
            :controls="isGenerated"
          />
        </Transition>
      </div>
    </div>
  </SectionTemplate>
</template>

<script setup lang="ts">
import BaseButton from '@/design/components/buttons/base.vue';
import SectionTemplate from '@/design/widgets/landing-section/cn-template.vue';
import { getFeUrl } from '@/utils/url';
import { useBreakpoint } from '@rendora/design/index';
import { nanoid } from 'nanoid';
import { addOpus } from '~/api';
import VideoPlayer from '~/components/video-player/index.vue';
import IconMoreRight from '~/design/icons/more-right.svg?component';
import ImageSelector from './imageSelector.vue';

import { clear, del, get, getMany, set } from 'idb-keyval';

const DB = { set, get, del, clear, getMany };

const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

const avatarImages = [
  {
    src: getFeUrl(
      '/youyan-platform/ai-avatar-video-generation/gen-like-typing/avatar-1.png?x-oss-process=image/format,webp',
    ),
    alt: '人物：海隽',
    key: 'F',
    identity: 'AF037_7875_new',
  },
  {
    src: getFeUrl(
      '/youyan-platform/ai-avatar-video-generation/gen-like-typing/avatar-2.png?x-oss-process=image/format,webp',
    ),
    alt: '人物：朱叶',
    key: 'M',
    identity: 'AM050_10024_new',
  },
];

const sceneImages = [
  {
    src: getFeUrl(
      '/youyan-platform/ai-avatar-video-generation/gen-like-typing/scene-1.png?x-oss-process=image/format,webp',
    ),
    alt: '场景：办公室',
    key: 'S1',
    identity: 'bust_bsns_office_mark_01',
  },
  {
    src: getFeUrl(
      '/youyan-platform/ai-avatar-video-generation/gen-like-typing/scene-2.png?x-oss-process=image/format,webp',
    ),
    alt: '场景：教室',
    key: 'S2',
    identity: 'bust_edu_classroom_01',
  },
];

const selectedScene = ref('S1');
const selectedAvatar = ref('F');
const isGenerated = ref(false);
const isModify = ref(false);
const videoScript = ref(
  '你好呀，欢迎来到魔珐有言——3D数字人AI视频生成平台！在这里，不用拍摄，不用剪辑，只要点击一下，一条高质量视频马上就能生成。无论培训还是教学，宣传还是汇报，都能轻松搞定。现在就来试试看，真的很简单！',
);

const descText = computed(() => {
  return isMobile.value
    ? `写下你想说的话，选个角色和场景，\n轻轻一点，一条专业视频就完成了。立刻试试看吧。`
    : `写下你想说的话，选个角色和场景，轻轻一点，一条专业视频就完成了。立刻试试看吧。`;
});

const getSceneIdentity = (key: string) => {
  const scene = sceneImages.find(item => item.key === key);
  return scene ? scene.identity : '';
};
const getAvatarIdentity = (key: string) => {
  const avatar = avatarImages.find(item => item.key === key);
  return avatar ? avatar.identity : '';
};

const handleSelectAvatar = (key: string) => {
  selectedAvatar.value = key;
  isGenerated.value = false;
};
const handleSelectScene = (key: string) => {
  selectedScene.value = key;
  isGenerated.value = false;
};

const standbyVideoSrc = computed(() => {
  return getFeUrl(
    `/youyan-platform/ai-avatar-video-generation/gen-like-typing/standby-${selectedAvatar.value}${selectedScene.value}.webm`,
  );
});

const standbyPosterSrc = computed(() => {
  return getFeUrl(
    `/youyan-platform/ai-avatar-video-generation/gen-like-typing/standby-${selectedAvatar.value}${selectedScene.value}.png?x-oss-process=image/format,webp`,
  );
});

const generateVideoSrc = computed(() => {
  return getFeUrl(
    `/youyan-platform/ai-avatar-video-generation/gen-like-typing/gen-${selectedAvatar.value}${selectedScene.value}.webm`,
  );
});

const generatePosterSrc = computed(() => {
  return getFeUrl(
    `/youyan-platform/ai-avatar-video-generation/gen-like-typing/gen-${selectedAvatar.value}${selectedScene.value}.png?x-oss-process=image/format,webp`,
  );
});

const handleGenerate = async () => {
  if (isModify.value) {
    if (isMobile.value) {
      emit('trial');
      return;
    }
    const create_data = {
      map_identity: getSceneIdentity(selectedScene.value),
      character_identity: getAvatarIdentity(selectedAvatar.value),
    };
    const youyanTempNewData = {
      segments: [
        {
          id: nanoid(5),
          text: videoScript.value,
        },
      ],
    };
    // 未登录跳转登录
    const userInfo = useUserInfo();
    if (!userInfo.value) {
      // 接入场景和角色等接口
      loginTask.add({
        type: IAfterLoginActionType.CREATE_WORK_WITH_PRESET,
        payload: { create_data, youyanTempNewData },
      });
      navigateTo(
        `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace`))}`,
      );
      return;
    }
    return addOpus(create_data as any).then(result => {
      if (result) {
        const data = result.data as any;
        DB.set('youyan-temp-new-data', youyanTempNewData);
        window.open(`/editor/${data.id}/${data.secret_id}`, '_blank');
      }
    });
  }
  isGenerated.value = true;
};

const emit = defineEmits<{
  (e: 'trial'): void;
}>();
</script>

<style lang="scss" scoped src="./styles/pc.scss" />
<style lang="scss" scoped src="./styles/mobile.scss" />
